/**
 * Read-only schema explorer for a Source (ADR-0026).
 *
 * Drills schemas → tables → columns via `GET /sources/{id}/introspect`. Pure
 * structure (names + types), never row data. Tray's own `tray_data_*` schemas
 * are hidden server-side. A live read of the backing DB, so it surfaces
 * connection errors inline rather than 500-ing.
 */
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Columns3, Loader2, Table2 } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Alert } from "@/components/ui/alert";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { sourcesApi, type Source } from "@/lib/api-sources";

export interface SourceExploreDialogProps {
  source: Source | null;
  onOpenChange: (open: boolean) => void;
}

export function SourceExploreDialog({ source, onOpenChange }: SourceExploreDialogProps) {
  const { t } = useTranslation();
  const [schema, setSchema] = React.useState<string | null>(null);
  const [table, setTable] = React.useState<string | null>(null);

  // Reset the drill whenever a different source opens.
  React.useEffect(() => {
    setSchema(null);
    setTable(null);
  }, [source?.id]);

  const open = source !== null;
  const level: "schemas" | "tables" | "columns" = table ? "columns" : schema ? "tables" : "schemas";

  const q = useQuery({
    queryKey: ["sources", source?.id, "introspect", level, schema, table],
    queryFn: () =>
      sourcesApi.introspect(source!.id, {
        level,
        schema: schema ?? "public",
        table: table ?? "",
      }),
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">
            {t("sources.explore.title", "Esplora")} · {source?.name}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {/* Breadcrumb drill */}
          <div className="mb-3 flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
            <button
              type="button"
              className="rounded px-1.5 py-0.5 hover:bg-muted hover:text-foreground"
              onClick={() => {
                setSchema(null);
                setTable(null);
              }}
            >
              {t("sources.explore.schemas", "Schemi")}
            </button>
            {schema && (
              <>
                <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
                <button
                  type="button"
                  className="rounded px-1.5 py-0.5 font-mono hover:bg-muted hover:text-foreground"
                  onClick={() => setTable(null)}
                >
                  {schema}
                </button>
              </>
            )}
            {table && (
              <>
                <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
                <span className="rounded px-1.5 py-0.5 font-mono text-foreground">{table}</span>
              </>
            )}
          </div>

          {q.isLoading ? (
            <div className="flex items-center gap-2 py-6 text-xs text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              {t("sources.explore.loading", "Lettura schema…")}
            </div>
          ) : q.isError ? (
            <Alert tone="danger" title={t("sources.explore.failed", "Esplorazione fallita")}>
              {(q.error as Error).message}
            </Alert>
          ) : level === "columns" ? (
            <div className="overflow-hidden rounded-lg border border-border/60">
              {(q.data?.columns ?? []).map((c) => (
                <div
                  key={c.name}
                  className="flex items-center justify-between border-b border-border/40 px-3 py-1.5 text-xs last:border-0"
                >
                  <span className="flex items-center gap-2 font-mono">
                    <Columns3 className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
                    {c.name}
                  </span>
                  <span className="text-muted-foreground">{c.type}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="max-h-80 space-y-0.5 overflow-y-auto">
              {(q.data?.items ?? []).map((item) => (
                <button
                  key={item}
                  type="button"
                  className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs hover:bg-muted"
                  onClick={() => (level === "schemas" ? setSchema(item) : setTable(item))}
                >
                  <Table2 className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
                  <span className="font-mono">{item}</span>
                  <ChevronRight className="ml-auto h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
                </button>
              ))}
              {(q.data?.items ?? []).length === 0 && (
                <p className="px-2.5 py-6 text-center text-xs text-muted-foreground">
                  {t("sources.explore.empty", "Niente qui.")}
                </p>
              )}
            </div>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
